import { cn, Calendar, Clock, X } from "@app/ui";
import { useState } from "react";
import { AnimatePresence, motion, springs, useReducedMotion } from "../../../../lib/motion/index.js";
import { useI18n } from "../../../../lib/useI18n.js";
import { useTimeFormat } from "../../../../lib/useTimeFormat.js";
import { useAppLocale } from "../../../../lib/useAppLocale.js";
import { DatePicker } from "../../../task-form/DatePicker.js";
import { TimePicker } from "../../../task-form/TimePicker.js";
import { toLocalDate } from "../../../task-form/time-helpers.js";
import { SheetModal } from "../SheetModal.js";

interface Props {
  dateStr: string;
  setDateStr: (s: string) => void;
  hour: number | null;
  setHour: (h: number | null) => void;
  minute: number | null;
  setMinute: (m: number | null) => void;
  allDay: boolean;
  setAllDay: (v: boolean) => void;
  duration: number;
  setDuration: (m: number) => void;
  isEvent: boolean;
  deadlineStr: string;
  setDeadlineStr: (s: string) => void;
}

const DUR_STEP = 15;
/** Hard floor for picker-derived durations: an explicit end time may go below
 *  the 15-min step (a deliberate short slot), but never to 0 — a 0-duration
 *  entry renders as a degenerate point block on the grid. */
const DUR_MIN = 5;

function parseDate(dateStr: string): Date | null {
  if (!dateStr) return null;
  const d = new Date(`${dateStr}T00:00`);
  return isNaN(d.getTime()) ? null : d;
}

function parseDateTime(dateStr: string, hour: number | null, minute: number | null): Date | null {
  const d = parseDate(dateStr);
  if (!d || hour == null || minute == null) return null;
  d.setHours(hour, minute, 0, 0);
  return d;
}

function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60_000);
}

function diffMinutes(from: Date, to: Date): number {
  return Math.max(0, Math.round((to.getTime() - from.getTime()) / 60_000));
}

function fmtDuration(min: number): string {
  if (min <= 0) return "0m";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return [h ? `${h}h` : "", m ? `${m}m` : ""].filter(Boolean).join(" ");
}

function fmtNumericDate(date: Date): string {
  return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}.`;
}

const chip = "flex items-center gap-1.5 rounded-md border bg-background px-3 py-2 text-sm";
const rangeGroup = "flex min-w-0 overflow-hidden rounded-md border bg-background text-sm";
const rangeButton = "flex min-w-0 flex-1 items-center gap-1.5 px-2.5 py-2";
const rangeSeparator = "grid w-6 shrink-0 place-items-center border-x text-muted-foreground";

/** Date / time / end time / deadline, editing through the app's own pickers in
 * a centered modal (never native inputs; they render off-screen on Android). */
export function ScheduleField(p: Props) {
  const { t } = useI18n();
  const tf = useTimeFormat();
  const locale = useAppLocale();
  const reduce = useReducedMotion();
  const [picker, setPicker] = useState<"date" | "time" | "endDate" | "endTime" | "deadline" | null>(null);
  const date = parseDate(p.dateStr);

  const timed = !!p.dateStr && !p.allDay;
  const hasTime = p.hour != null && p.minute != null;
  const startAt = parseDateTime(p.dateStr, p.hour, p.minute);
  const endAt = startAt ? addMinutes(startAt, Math.max(0, p.duration)) : null;
  const transition = reduce ? { duration: 0 } : springs.snappy;

  const setEndDate = (d: Date) => {
    if (!startAt) return;
    const base = endAt ?? startAt;
    const next = new Date(d);
    next.setHours(base.getHours(), base.getMinutes(), 0, 0);
    p.setDuration(Math.max(DUR_MIN, diffMinutes(startAt, next)));
    setPicker(null);
  };

  const setEndTime = (hour: number | null, minute: number | null) => {
    if (!startAt || hour == null || minute == null) return;
    const next = endAt ? new Date(endAt) : new Date(startAt);
    next.setHours(hour, minute, 0, 0);
    if (next.getTime() < startAt.getTime()) next.setDate(next.getDate() + 1);
    p.setDuration(Math.max(DUR_MIN, diffMinutes(startAt, next)));
  };

  return (
    <div className="flex flex-col gap-2 px-1">
      <motion.div layout={!reduce} transition={transition} className="flex items-center gap-2">
        <motion.div layout={!reduce} transition={transition} className={cn(rangeGroup, timed ? "flex-1" : "min-w-0 shrink")}>
          <button type="button" className={rangeButton} onClick={() => setPicker("date")}>
            <Calendar className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{date ? fmtNumericDate(date) : t("taskForm.setDate")}</span>
          </button>
          <AnimatePresence initial={false} mode="popLayout">
            {timed && (
              <motion.span
                key="date-dash"
                aria-hidden
                className={rangeSeparator}
                initial={reduce ? false : { opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 24 }}
                exit={reduce ? { opacity: 0, width: 0 } : { opacity: 0, width: 0 }}
                transition={transition}
              >
                -
              </motion.span>
            )}
          </AnimatePresence>
          <AnimatePresence initial={false} mode="popLayout">
            {timed && (
              <motion.button
                key="end-date"
                layout={!reduce}
                type="button"
                className={rangeButton}
                onClick={() => setPicker("endDate")}
                initial={reduce ? false : { opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: "auto" }}
                exit={reduce ? { opacity: 0, width: 0 } : { opacity: 0, width: 0 }}
                transition={transition}
              >
                <span className="truncate">{endAt ? fmtNumericDate(endAt) : t("taskForm.endDate")}</span>
              </motion.button>
            )}
          </AnimatePresence>
        </motion.div>

        {p.dateStr && (
          <motion.button
            layout={!reduce}
            type="button"
            className={cn(
              chip,
              "min-w-[4.75rem] shrink justify-center overflow-hidden transition-colors duration-200 ease-out",
              p.allDay && "bg-primary/50 text-primary-foreground",
            )}
            animate={{ flexGrow: p.allDay ? 1 : 0 }}
            onClick={() => p.setAllDay(!p.allDay)}
            transition={transition}
          >
            {t("taskForm.allDay")}
          </motion.button>
        )}

        {p.dateStr && (
          <motion.button
            layout={!reduce}
            type="button"
            aria-label={t("taskForm.clearDate")}
            className="ml-auto -mr-1 shrink-0 rounded-md bg-transparent p-2 text-muted-foreground"
            onClick={() => { p.setDateStr(""); p.setHour(null); p.setMinute(null); }}
            transition={transition}
          >
            <X className="h-4 w-4" />
          </motion.button>
        )}
      </motion.div>

      <AnimatePresence initial={false} mode="popLayout">
        {timed && (
          <motion.div
            key="end"
            layout={!reduce}
            className="flex items-center gap-2 overflow-hidden"
            initial={reduce ? false : { opacity: 0, height: 0, y: -4 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={reduce ? { opacity: 0, height: 0 } : { opacity: 0, height: 0, y: -4 }}
            transition={transition}
          >
            <div className={cn(rangeGroup, "flex-1")}>
              <button type="button" className={rangeButton} onClick={() => setPicker("time")}>
                <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
                {hasTime ? tf.hm(p.hour!, p.minute!) : t("taskForm.setTime")}
              </button>
              <span aria-hidden className={rangeSeparator}>-</span>
              <button type="button" className={rangeButton} onClick={() => setPicker("endTime")}>
                <span className="truncate">{endAt ? tf.hm(endAt.getHours(), endAt.getMinutes()) : t("taskForm.end")}</span>
              </button>
            </div>
            <div className="flex shrink-0 items-center gap-1 rounded-md border bg-background px-1">
              <button
                type="button"
                className="px-1.5 py-1.5 text-lg leading-none"
                aria-label={t("taskForm.shorter")}
                // Stepping stops at one step; a shorter-still slot is set via the
                // explicit end-time picker (which floors at DUR_MIN, never 0).
                onClick={() => { if (p.duration > DUR_STEP) p.setDuration(Math.max(DUR_STEP, p.duration - DUR_STEP)); }}
              >
                -
              </button>
              <span className="min-w-12 text-center text-sm">{fmtDuration(p.duration)}</span>
              <button
                type="button"
                className="px-1.5 py-1.5 text-lg leading-none"
                aria-label={t("taskForm.longer")}
                onClick={() => p.setDuration(p.duration + DUR_STEP)}
              >
                +
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!p.isEvent && (
        <button type="button" className={chip} onClick={() => setPicker("deadline")}>
          <Calendar className="h-4 w-4 text-muted-foreground" />
          {p.deadlineStr
            ? `${t("taskForm.deadline")} - ${new Date(`${p.deadlineStr}T00:00`).toLocaleDateString(locale, { month: "short", day: "numeric" })}`
            : t("taskForm.addDeadline")}
          {p.deadlineStr && (
            <X className="ml-1 h-4 w-4" onClick={(e) => { e.stopPropagation(); p.setDeadlineStr(""); }} />
          )}
        </button>
      )}

      <SheetModal open={picker === "date"} onClose={() => setPicker(null)}>
        <DatePicker
          value={date}
          onSelect={(d) => {
            p.setDateStr(toLocalDate(d));
            if (!p.allDay && p.hour == null) { p.setHour(9); p.setMinute(0); }
            setPicker(null);
          }}
        />
      </SheetModal>

      <SheetModal open={picker === "time"} onClose={() => setPicker(null)}>
        <div className="flex flex-col gap-2">
          <TimePicker
            hour={p.hour}
            minute={p.minute}
            onChange={(h, m) => { p.setHour(h); p.setMinute(m); }}
          />
          <button type="button" className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground" onClick={() => setPicker(null)}>{t("common.done")}</button>
        </div>
      </SheetModal>

      <SheetModal open={picker === "endDate"} onClose={() => setPicker(null)}>
        <DatePicker value={endAt} onSelect={setEndDate} />
      </SheetModal>

      <SheetModal open={picker === "endTime"} onClose={() => setPicker(null)}>
        <div className="flex flex-col gap-2">
          <TimePicker
            hour={endAt?.getHours() ?? null}
            minute={endAt?.getMinutes() ?? null}
            onChange={setEndTime}
          />
          <button type="button" className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground" onClick={() => setPicker(null)}>{t("common.done")}</button>
        </div>
      </SheetModal>

      <SheetModal open={picker === "deadline"} onClose={() => setPicker(null)}>
        <DatePicker
          value={parseDate(p.deadlineStr)}
          onSelect={(d) => { p.setDeadlineStr(toLocalDate(d)); setPicker(null); }}
        />
      </SheetModal>
    </div>
  );
}
